/**
 * The 2026 programme, reported rather than measured.
 *
 * The deck's third slide — staff training, the new global network, 24/7
 * monitoring — is the part of the security story NOVA cannot see. Training
 * completion lives in the LMS, the site count lives with the network programme,
 * and the SOC contract lives with Legal. None of them is telemetry.
 *
 * **These figures are curated constants, typed in by hand from the Q2 2026
 * leadership deck. There is no data source behind them and none is implied.**
 * That is the honest option and the deliberate one: inventing a feed — deriving
 * "training completion" from something adjacent, or counting Cato sites from the
 * network board and calling it the rollout — would produce a number that looks
 * measured, moves on its own, and disagrees with the programme owner. A stale
 * figure whose provenance is on the card is safer than a live figure whose
 * definition is wrong.
 *
 * So every row carries its figure, its as-of date and its owner, and is badged
 * "Reported, not measured" — the same discipline as the CIO scorecard. When a
 * row becomes measurable, delete it from this constant and give it a panel; do
 * not quietly start filling it from an API.
 *
 * Updating it: edit `PROGRAMME_ROWS` below, and move `AS_OF` with it. If the
 * as-of date is older than the last leadership update, the card says so rather
 * than presenting an old number as current.
 */

import { Calendar, Icon, type LucideIcon, Network, ShieldCheck, Users } from "@norican/icons";
import { Badge, Meter, Typography } from "@norican/react";

import type { Verdict } from "@/components/board";

import { PanelCard } from "./panel-card";
import { formatDayLabel, meterTone, type StatusTone } from "./status";

/**
 * The reporting date of the source these figures were read from.
 *
 * Source: `executive_leadership_meeting/content/deck.yaml`, "Q2 2026 Leadership
 * Update", dated 2026-07-02.
 */
const AS_OF = "2026-07-02";

/** Who owns the number — the person to ask when it looks wrong. */
const OWNER = "VP Security & Compliance";

/** How stale a curated figure may be before the card flags it, in days. */
const STALE_AFTER_DAYS = 120;

interface ProgrammeRow {
  id: string;
  icon: LucideIcon;
  /** What the initiative is, in the deck's words. */
  title: string;
  /** The reported figure, already formatted. */
  figure: string;
  /** Progress against its target, 0–100, when the row has one. */
  progressPct?: number;
  /** What the progress bar is measured against. */
  target?: string;
  /** Status tone for the row, as the deck tones its own card. */
  tone: StatusTone;
  /** The status sentence from the deck's own card footer. */
  status: string;
}

/** Curated. Hand-maintained. No API behind any of these — see the file header. */
const PROGRAMME_ROWS: readonly ProgrammeRow[] = [
  {
    id: "training",
    icon: Users,
    title: "Staff training — mandatory annual programme",
    figure: "87% completion",
    progressPct: 87,
    target: "95% by end September",
    tone: "success",
    status: "On track. No decision needed today.",
  },
  {
    id: "network",
    icon: Network,
    title: "New global network — replacing the legacy setup",
    figure: "4 of 15 sites live",
    progressPct: (4 / 15) * 100,
    target: "8 sites by end September, full cutover by end 2026",
    tone: "warning",
    status: "On track for the end-2026 cutover. Plan and budget already confirmed.",
  },
  {
    id: "soc",
    icon: ShieldCheck,
    title: "24/7 monitoring — external partner",
    figure: "Partner selected, contract in review",
    target: "Go-live 1 October 2026",
    tone: "warning",
    status: "Needs leadership endorsement of the supplier security clauses this week.",
  },
];

export function ProgrammeStatus() {
  const ageDays = Math.floor((Date.now() - new Date(`${AS_OF}T00:00:00Z`).getTime()) / 86_400_000);
  const stale = ageDays > STALE_AFTER_DAYS;

  return (
    <section id="security-programme" className="scroll-mt-6">
      <PanelCard
        title="The 2026 plan"
        caption={
          <span className="inline-flex items-center gap-2">
            <Icon icon={Calendar} size="xs" />
            As of {formatDayLabel(AS_OF)} · {OWNER}
          </span>
        }
        verdict={programmeVerdict(stale, ageDays)}
        note={
          <Typography variant="caption" tone="muted">
            Every figure on this card is <strong>reported, not measured</strong>. They are typed in
            by hand from the quarterly leadership update; NOVA has no feed for training completion,
            the network rollout or the SOC contract, and deriving one from something adjacent would
            produce a number that looks live and disagrees with the programme owner. Ask {OWNER}{" "}
            when a figure looks wrong — the fix is a new reported figure, not a query.
          </Typography>
        }
      >
        <div className="flex flex-col gap-5">
          {PROGRAMME_ROWS.map((row) => (
            <div key={row.id} className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-2">
                  <Icon icon={row.icon} size="sm" />
                  <Typography variant="body-sm">{row.title}</Typography>
                  <Badge tone="neutral" variant="outline" size="xs">
                    Reported, not measured
                  </Badge>
                </span>
                <Typography variant="body-sm" tabular>
                  {row.figure}
                </Typography>
              </div>

              {row.progressPct !== undefined ? (
                <Meter.Root
                  value={row.progressPct}
                  min={0}
                  max={100}
                  size="sm"
                  tone={meterTone(row.tone)}
                  aria-valuetext={`${row.title}: ${row.figure}${row.target ? `, target ${row.target}` : ""}`}
                >
                  <Meter.Track>
                    <Meter.Indicator />
                  </Meter.Track>
                </Meter.Root>
              ) : null}

              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                {row.target && (
                  <Typography variant="caption" tone="muted">
                    Target: {row.target}
                  </Typography>
                )}
                <Typography variant="caption" tone="muted">
                  {row.status}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </PanelCard>
    </section>
  );
}

/** Whether the curated figures are still worth reading. */
function programmeVerdict(stale: boolean, ageDays: number): Verdict {
  if (stale) {
    return {
      label: "Stale",
      tone: "warning",
      text: `These figures are ${ageDays} days old and predate the current reporting period. Refresh them with ${OWNER} before quoting them.`,
    };
  }
  return {
    label: "Reported",
    tone: "neutral",
    text: `Curated figures from the last leadership update, ${ageDays} days old. One decision is outstanding: the supplier security clauses for 24/7 monitoring.`,
  };
}
